"use client";

/**
 * RichNoteEditor — Google-Doc-ish note editor for leads.
 *
 * One big clean writing surface with basic formatting (bold, italic,
 * underline, bullets, numbered lists, text color, highlight) and the same
 * @mention support the old textarea had. Zero new dependencies: a
 * contentEditable div + execCommand for the five formatting primitives.
 *
 * Emits sanitized-ish HTML upward; the SERVER is the real sanitizer
 * (lead-notes.service allowlists tags/styles before DLP masking +
 * encryption), and render paths sanitize again via noteHtml.ts.
 *
 * Auto-save: parent passes onAutoSave — fired on blur (leaving the editor)
 * when there's unsaved content, Google-Docs style.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Bold, Italic, Underline, Strikethrough, List, ListOrdered, Baseline, Highlighter,
  RemoveFormatting, AtSign, Type, Quote, Minus, Undo2, Redo2, ChevronDown,
  Link2, ImagePlus, Loader2, ClipboardList,

  Mic,
  Square,} from "lucide-react";

import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { cn } from "@/app/_module/utils/utils";
import type { NoteMention } from "../types/create-lead-note";
import {
  startSpeakIQ,
  speakIQSupported,
  SpeakIQError,
  type SpeakIQSession,
  type SpeakIQStage,
} from "../services/speakIQ";
import { sanitizeNoteHtml } from "./noteHtml";

interface AgentOption {
  id: string;
  name: string;
  email: string;
  isSelf?: boolean;
}

const TEXT_COLORS = ["#111827", "#dc2626", "#ea580c", "#16a34a", "#2563eb", "#7c3aed"];
const HIGHLIGHTS = ["#fef08a", "#bbf7d0", "#bfdbfe", "#fbcfe8"];
const FONT_SIZES = [
  { label: "Small", px: "12px" },
  { label: "Normal", px: "14px" },
  { label: "Large", px: "18px" },
  { label: "Huge", px: "24px" },
];

interface RichNoteEditorProps {
  /** Initial HTML (or plain text) — editor is uncontrolled after mount; remount with `key` to reset */
  initialContent?: string;
  onChange: (html: string, text: string, mentions: NoteMention[]) => void;
  /** Fired when focus leaves the editor with content present (auto-save hook) */
  onAutoSave?: () => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
  /**
   * Uploads an image and resolves its hosted URL (null on failure). When
   * provided, the toolbar shows an image button and image paste works —
   * images embed inline like a Word doc.
   */
  onImageUpload?: (file: File) => Promise<string | null>;
  /**
   * Optional insert-template toolbar button (e.g. "Client Info Sheet"). When
   * provided, a button appears in the toolbar that drops `html` in at the
   * cursor — so the template lands inside the editor, not as a separate row.
   */
  template?: { html: string; title: string };
}

export function RichNoteEditor({
  initialContent = "",
  onChange,
  onAutoSave,
  placeholder = "Write your note… select text to format, @ to mention an agent",
  className,
  minHeight = "220px",
  onImageUpload,
  template,
}: RichNoteEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [showColors, setShowColors] = useState<"text" | "highlight" | "size" | null>(null);
  const [showLink, setShowLink] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const savedRangeRef = useRef<Range | null>(null);

  // ── @mention state ──────────────────────────────────────────────────────
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionPos, setMentionPos] = useState<{ top: number; left: number } | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { user } = useUserContext();
  const currentUserId = (user?.user as any)?.id;
  const isAgencyUser = !!user?.currentAgency?.agency?.id;

  const { data: agentsData } = useQuery<AgentOption[]>({
    queryKey: ["agency-agents-mention"],
    queryFn: async () => {
      const result = await handleApiCall<AgentOption[]>(async () => {
        return api.get<{ data: AgentOption[] }>({
          url: getApiUrl(endpoint.agencies.agents) + "?includeHierarchy=true",
        });
      });
      return result || [];
    },
    enabled: isAgencyUser,
    staleTime: 5 * 60 * 1000,
  });

  const filteredAgents = useMemo(() => {
    if (!agentsData || mentionQuery === null) return [];
    const q = mentionQuery.toLowerCase();
    return agentsData
      .filter((a) => !a.isSelf && a.id !== currentUserId)
      .filter((a) => !q || a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q))
      .slice(0, 8);
  }, [agentsData, mentionQuery, currentUserId]);

  useEffect(() => setSelectedIndex(0), [filteredAgents.length]);

  // ── content plumbing ────────────────────────────────────────────────────
  const collectMentions = useCallback((): NoteMention[] => {
    const el = editorRef.current;
    if (!el) return [];
    const seen = new Set<string>();
    const out: NoteMention[] = [];
    el.querySelectorAll("[data-mention-id]").forEach((span) => {
      const id = span.getAttribute("data-mention-id") || "";
      const name = span.getAttribute("data-mention-name") || span.textContent?.replace(/^@/, "") || "";
      if (id && !seen.has(id)) {
        seen.add(id);
        out.push({ userId: id, name } as NoteMention);
      }
    });
    return out;
  }, []);

  const emit = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const text = el.innerText.trim();
    // An image with no text is still content — must save, not read as empty.
    const hasContent = text.length > 0 || !!el.querySelector("img");
    setIsEmpty(!hasContent);
    // Unformatted single-line content has no element markup — innerHTML would
    // be entity-escaped text ("A &amp; B") that the plain-text render path
    // shows literally. Emit the plain text instead; HTML only when formatted.
    const html = el.innerHTML;
    const body = /<[a-z][^>]*>/i.test(html) ? html : text;
    onChange(!hasContent ? "" : body, text, collectMentions());
  }, [onChange, collectMentions]);

  // Seed initial content once
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (initialContent) {
      if (/<[a-z][\s\S]*>/i.test(initialContent)) {
        // Sanitize BEFORE assigning — initialContent comes from storage
        // (drafts, existing notes); never trust it into innerHTML raw.
        el.innerHTML = sanitizeNoteHtml(initialContent);
      } else {
        el.innerText = initialContent; // legacy plain text — never parse as HTML
      }
      setIsEmpty(el.innerText.trim().length === 0 && !el.querySelector("img"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── formatting ──────────────────────────────────────────────────────────
  const exec = useCallback((command: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    emit();
  }, [emit]);

  // Font size: execCommand('fontSize') emits legacy <font size="7"> tags —
  // apply it as a marker, then convert those to clean <span style="font-size">
  // (which our sanitizers allow; <font> would be stripped server-side).
  const applyFontSize = useCallback((px: string) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    document.execCommand("fontSize", false, "7");
    el.querySelectorAll('font[size="7"]').forEach((font) => {
      const span = document.createElement("span");
      span.style.fontSize = px;
      span.innerHTML = (font as HTMLElement).innerHTML;
      font.replaceWith(span);
    });
    emit();
  }, [emit]);

  // ── links & images ──────────────────────────────────────────────────────
  const saveSelection = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
  }, []);

  const restoreSelection = useCallback(() => {
    const range = savedRangeRef.current;
    if (!range) return;
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, []);

  const applyLink = useCallback(() => {
    let url = linkUrl.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
    // Keep attribute context inert — the server sanitizer is the real gate.
    const safeUrl = url.replace(/"/g, "%22").replace(/</g, "%3C").replace(/>/g, "%3E");
    editorRef.current?.focus();
    restoreSelection();
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) {
      document.execCommand("insertHTML", false, `<a href="${safeUrl}">${safeUrl}</a>&nbsp;`);
    } else {
      document.execCommand("createLink", false, safeUrl);
    }
    editorRef.current?.querySelectorAll("a").forEach((a) => {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer nofollow");
    });
    setShowLink(false);
    setLinkUrl("");
    emit();
  }, [linkUrl, restoreSelection, emit]);

  // ── SpeakIQ (talk-to-type) ───────────────────────────────────────────────
  // Talk-to-type, entirely on this machine. Reuses insertAtCursor below so the
  // dictated sentence lands where the agent is typing, not appended at the end.
  const [speaking, setSpeaking] = useState<SpeakIQStage | null>(null);
  const speakIQRef = useRef<SpeakIQSession | null>(null);
  const canSpeakIQ = useMemo(() => speakIQSupported(), []);

  const insertText = useCallback((text: string) => {
    const el = editorRef.current;
    if (!el || !text) return;
    el.focus();
    // Escape before inserting: dictation is user speech, but it travels through
    // a model, and this editor stores HTML.
    const safe = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    document.execCommand("insertHTML", false, `${safe} `);
    setIsEmpty(el.innerText.trim().length === 0 && !el.querySelector("img"));
    emit();
  }, [emit]);

  const finishSpeakIQ = useCallback(async () => {
    const session = speakIQRef.current;
    if (!session) return;
    speakIQRef.current = null;
    try {
      const text = await session.stop();
      if (text) insertText(text);
    } catch (err) {
      const msg = err instanceof SpeakIQError ? err.message : "SpeakIQ couldn't finish that.";
      // Surfaced inline rather than as a toast — the editor may be inside a
      // modal where toasts render behind the overlay.
      setSpeakIQError(msg);
      window.setTimeout(() => setSpeakIQError(null), 4000);
    } finally {
      setSpeaking(null);
    }
  }, [insertText]);

  const toggleSpeakIQ = useCallback(async () => {
    if (speakIQRef.current) return void finishSpeakIQ();
    setSpeakIQError(null);
    try {
      speakIQRef.current = await startSpeakIQ(setSpeaking);
    } catch (err) {
      const msg = err instanceof SpeakIQError ? err.message : "Couldn't start the microphone.";
      setSpeakIQError(msg);
      setSpeaking(null);
      window.setTimeout(() => setSpeakIQError(null), 5000);
    }
  }, [finishSpeakIQ]);

  const [speakIQError, setSpeakIQError] = useState<string | null>(null);

  // Never leave the microphone open if the editor unmounts mid-dictation.
  useEffect(() => () => speakIQRef.current?.cancel(), []);

  // Insert a fill-in template (e.g. Client Info Sheet) at the cursor. Sanitize
  // first — same gate as initialContent — then let the input event + emit()
  // propagate the new HTML up to the parent.
  const insertTemplate = useCallback((html: string) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    document.execCommand("insertHTML", false, sanitizeNoteHtml(html));
    setIsEmpty(el.innerText.trim().length === 0 && !el.querySelector("img"));
    emit();
  }, [emit]);

  const insertImageFile = useCallback(async (file: File) => {
    if (!onImageUpload || uploadingImage) return;
    if (!file.type.startsWith("image/")) return;
    setUploadingImage(true);
    try {
      const url = await onImageUpload(file);
      if (url) {
        editorRef.current?.focus();
        restoreSelection();
        const safeUrl = url.replace(/"/g, "%22").replace(/</g, "%3C").replace(/>/g, "%3E");
        // Insert small (360px) — click the image to drag-resize it.
        document.execCommand("insertHTML", false, `<img src="${safeUrl}" width="360" alt="" /><br />`);
        emit();
      }
    } finally {
      setUploadingImage(false);
    }
  }, [onImageUpload, uploadingImage, restoreSelection, emit]);

  // ── image select + drag-resize ──────────────────────────────────────────
  const selectedImgRef = useRef<HTMLImageElement | null>(null);
  const [resizeHandle, setResizeHandle] = useState<{ top: number; left: number } | null>(null);

  const positionResizeHandle = useCallback((img: HTMLImageElement) => {
    const surface = editorRef.current?.parentElement; // the position:relative wrapper
    if (!surface) return;
    const imgRect = img.getBoundingClientRect();
    const surfRect = surface.getBoundingClientRect();
    setResizeHandle({ top: imgRect.bottom - surfRect.top - 7, left: imgRect.right - surfRect.left - 7 });
  }, []);

  const deselectImage = useCallback(() => {
    if (selectedImgRef.current) selectedImgRef.current.style.outline = "";
    selectedImgRef.current = null;
    setResizeHandle(null);
  }, []);

  const handleEditorClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target instanceof HTMLImageElement && editorRef.current?.contains(target)) {
      deselectImage();
      selectedImgRef.current = target;
      target.style.outline = "2px solid #3b82f6";
      positionResizeHandle(target);
    } else {
      deselectImage();
    }
  }, [deselectImage, positionResizeHandle]);

  const startImageResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const img = selectedImgRef.current;
    const editor = editorRef.current;
    if (!img || !editor) return;
    const startX = e.clientX;
    const startW = img.getBoundingClientRect().width;
    const maxW = editor.clientWidth - 32;
    const onMove = (me: MouseEvent) => {
      const w = Math.round(Math.max(80, Math.min(startW + (me.clientX - startX), maxW)));
      img.setAttribute("width", String(w));
      img.removeAttribute("height"); // keep aspect ratio
      positionResizeHandle(img);
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      emit();
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [positionResizeHandle, emit]);

  // ── @mention detection ──────────────────────────────────────────────────
  const checkForMention = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || !sel.isCollapsed || !sel.anchorNode || sel.anchorNode.nodeType !== Node.TEXT_NODE) {
      setMentionQuery(null);
      return;
    }
    const textBefore = (sel.anchorNode.textContent || "").slice(0, sel.anchorOffset);
    const m = textBefore.match(/(?:^|\s)@([\w .-]{0,30})$/);
    if (!m) {
      setMentionQuery(null);
      return;
    }
    setMentionQuery(m[1]);
    const range = sel.getRangeAt(0).cloneRange();
    const rect = range.getBoundingClientRect();
    setMentionPos({ top: rect.bottom + 6, left: rect.left });
  }, []);

  const insertMention = useCallback((agent: AgentOption) => {
    const sel = window.getSelection();
    if (!sel || !sel.anchorNode || sel.anchorNode.nodeType !== Node.TEXT_NODE) return;
    const node = sel.anchorNode as Text;
    const textBefore = (node.textContent || "").slice(0, sel.anchorOffset);
    const m = textBefore.match(/(?:^|\s)@([\w .-]{0,30})$/);
    if (!m) return;
    const start = textBefore.lastIndexOf("@");
    // Split out the "@query" text and replace it with a mention chip
    const range = document.createRange();
    range.setStart(node, start);
    range.setEnd(node, sel.anchorOffset);
    range.deleteContents();
    const chip = document.createElement("span");
    chip.setAttribute("data-mention-id", agent.id);
    chip.setAttribute("data-mention-name", agent.name);
    chip.setAttribute("contenteditable", "false");
    chip.setAttribute("style", "color: #2563eb; font-weight: 600;");
    chip.textContent = `@${agent.name}`;
    range.insertNode(chip);
    const space = document.createTextNode(" ");
    chip.after(space);
    // caret after the space
    const after = document.createRange();
    after.setStartAfter(space);
    after.collapse(true);
    sel.removeAllRanges();
    sel.addRange(after);
    setMentionQuery(null);
    emit();
  }, [emit]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (mentionQuery !== null && filteredAgents.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIndex((i) => (i + 1) % filteredAgents.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setSelectedIndex((i) => (i - 1 + filteredAgents.length) % filteredAgents.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insertMention(filteredAgents[selectedIndex]); return; }
      if (e.key === "Escape") { setMentionQuery(null); return; }
    }
    // Keyboard shortcuts
    if ((e.metaKey || e.ctrlKey) && !e.shiftKey) {
      // Same guard as QuickActionsCommand: synthetic key events (password
      // managers, extensions) can arrive with no `key`. Lower risk here since
      // a modifier must already be held, but it is the same crash.
      const k = e.key?.toLowerCase();
      if (k === "b") { e.preventDefault(); exec("bold"); }
      if (k === "i") { e.preventDefault(); exec("italic"); }
      if (k === "u") { e.preventDefault(); exec("underline"); }
    }
  }, [mentionQuery, filteredAgents, selectedIndex, insertMention, exec]);

  const handleBlur = useCallback((e: React.FocusEvent) => {
    // Ignore focus moves within THIS editor's outermost rich-note scope —
    // its own toolbar/pickers, or a WRAPPING composer (selects, Save button)
    // that opted in with data-rich-note-root. Must be scope-SPECIFIC: another
    // editor elsewhere on the page carries the same attribute, and focusing
    // it is genuinely leaving this one.
    if (e.relatedTarget) {
      let scope = (e.currentTarget as HTMLElement).closest("[data-rich-note-root]");
      while (scope?.parentElement?.closest("[data-rich-note-root]")) {
        scope = scope.parentElement.closest("[data-rich-note-root]");
      }
      if (scope && scope.contains(e.relatedTarget as Node)) return;
    }
    setShowColors(null);
    setMentionQuery(null);
    deselectImage();
    const el = editorRef.current;
    if (el && (el.innerText.trim().length > 0 || el.querySelector("img"))) {
      onAutoSave?.();
    }
  }, [onAutoSave, deselectImage]);

  const ToolbarButton = ({ onClick, title, children, active = false }: any) => (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()} // keep the text selection
      onClick={onClick}
      className={cn(
        "p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors",
        active && "bg-gray-200",
      )}
    >
      {children}
    </button>
  );

  return (
    <div data-rich-note-root className={cn("rounded-lg border border-gray-200 bg-white focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/20", className)}>
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 border-b border-gray-100 px-2 py-1.5 flex-wrap">
        <div className="relative">
          <button
            type="button"
            title="Text size"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setShowColors(showColors === "size" ? null : "size")}
            className={cn("flex items-center gap-0.5 px-1.5 py-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors", showColors === "size" && "bg-gray-200")}
          >
            <Type className="h-4 w-4" />
            <ChevronDown className="h-3 w-3" />
          </button>
          {showColors === "size" && (
            <div className="absolute top-full left-0 mt-1 z-30 w-28 rounded-md border bg-white py-1 shadow-lg">
              {FONT_SIZES.map((size) => (
                <button
                  key={size.px}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { applyFontSize(size.px); setShowColors(null); }}
                  className="w-full px-3 py-1 text-left hover:bg-gray-50 text-gray-800"
                  style={{ fontSize: size.px }}
                >
                  {size.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="w-px h-5 bg-gray-200 mx-1" />
        <ToolbarButton title="Bold (⌘B)" onClick={() => exec("bold")}><Bold className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Italic (⌘I)" onClick={() => exec("italic")}><Italic className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Underline (⌘U)" onClick={() => exec("underline")}><Underline className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Strikethrough" onClick={() => exec("strikeThrough")}><Strikethrough className="h-4 w-4" /></ToolbarButton>
        <div className="w-px h-5 bg-gray-200 mx-1" />
        <ToolbarButton title="Bullet list" onClick={() => exec("insertUnorderedList")}><List className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Numbered list" onClick={() => exec("insertOrderedList")}><ListOrdered className="h-4 w-4" /></ToolbarButton>
        <div className="w-px h-5 bg-gray-200 mx-1" />
        <div className="relative">
          <ToolbarButton title="Text color" onClick={() => setShowColors(showColors === "text" ? null : "text")} active={showColors === "text"}>
            <Baseline className="h-4 w-4" />
          </ToolbarButton>
          {showColors === "text" && (
            <div className="absolute top-full left-0 mt-1 z-30 flex gap-1 rounded-md border bg-white p-1.5 shadow-lg">
              {TEXT_COLORS.map((c) => (
                <button key={c} type="button" onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { exec("foreColor", c); setShowColors(null); }}
                  className="h-5 w-5 rounded-full border border-gray-200 hover:scale-110 transition-transform"
                  style={{ backgroundColor: c }} title={c} />
              ))}
            </div>
          )}
        </div>
        <div className="relative">
          <ToolbarButton title="Highlight" onClick={() => setShowColors(showColors === "highlight" ? null : "highlight")} active={showColors === "highlight"}>
            <Highlighter className="h-4 w-4" />
          </ToolbarButton>
          {showColors === "highlight" && (
            <div className="absolute top-full left-0 mt-1 z-30 flex gap-1 rounded-md border bg-white p-1.5 shadow-lg">
              {HIGHLIGHTS.map((c) => (
                <button key={c} type="button" onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { exec("hiliteColor", c); setShowColors(null); }}
                  className="h-5 w-5 rounded-full border border-gray-200 hover:scale-110 transition-transform"
                  style={{ backgroundColor: c }} title={c} />
              ))}
              <button type="button" onMouseDown={(e) => e.preventDefault()}
                onClick={() => { exec("hiliteColor", "transparent"); setShowColors(null); }}
                className="h-5 w-5 rounded-full border border-gray-300 bg-white text-[9px] text-gray-500 leading-5"
                title="Remove highlight">✕</button>
            </div>
          )}
        </div>
        <div className="w-px h-5 bg-gray-200 mx-1" />
        <ToolbarButton
          title="Insert link"
          active={showLink}
          onClick={() => {
            saveSelection();
            setShowLink((v) => !v);
            setLinkUrl("");
          }}
        >
          <Link2 className="h-4 w-4" />
        </ToolbarButton>
        {onImageUpload && (
          <ToolbarButton
            title="Insert image"
            onClick={() => {
              if (uploadingImage) return;
              saveSelection();
              fileInputRef.current?.click();
            }}
          >
            {uploadingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          </ToolbarButton>
        )}
        {template && (
          <ToolbarButton title={template.title} onClick={() => insertTemplate(template.html)}>
            <ClipboardList className="h-4 w-4 text-blue-600" />
          </ToolbarButton>
        )}
        {canSpeakIQ && (
          <ToolbarButton
            title={speaking ? "SpeakIQ — stop and insert" : "SpeakIQ — speak your note"}
            onClick={toggleSpeakIQ}
          >
            {speaking ? (
              <Square className="h-3.5 w-3.5 fill-red-600 text-red-600" />
            ) : (
              <Mic className="h-4 w-4 text-violet-600" />
            )}
          </ToolbarButton>
        )}
        <div className="w-px h-5 bg-gray-200 mx-1" />
        <ToolbarButton title="Quote block" onClick={() => exec("formatBlock", "blockquote")}><Quote className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Divider line" onClick={() => exec("insertHorizontalRule")}><Minus className="h-4 w-4" /></ToolbarButton>
        <div className="w-px h-5 bg-gray-200 mx-1" />
        <ToolbarButton title="Undo" onClick={() => exec("undo")}><Undo2 className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Redo" onClick={() => exec("redo")}><Redo2 className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Clear formatting" onClick={() => exec("removeFormat")}><RemoveFormatting className="h-4 w-4" /></ToolbarButton>
        <span className="ml-auto flex items-center gap-1 text-[10px] text-gray-400 pr-1">
          <AtSign className="h-3 w-3" /> mention · autosaves when you click away
        </span>
      </div>

      {/* SpeakIQ status. Sits under the toolbar so the editor never shifts. */}
      {(speaking || speakIQError) && (
        <div className="flex items-center gap-2 border-b border-gray-100 bg-violet-50/70 px-2 py-1.5 text-[11px] text-violet-800">
          {speakIQError ? (
            <span className="text-red-600">{speakIQError}</span>
          ) : speaking?.stage === "listening" ? (
            <>
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-600" />
              </span>
              SpeakIQ listening — {speaking.seconds}s
              <button
                type="button"
                onClick={toggleSpeakIQ}
                className="ml-1 rounded bg-violet-600 px-1.5 py-0.5 text-[10px] font-semibold text-white"
              >
                Stop &amp; insert
              </button>
              <span className="ml-auto text-violet-500">runs on your device</span>
            </>
          ) : speaking?.stage === "requesting-mic" ? (
            <>Waiting for microphone permission…</>
          ) : speaking?.stage === "transcribing" ? (
            <>Transcribing what you said…</>
          ) : speaking?.stage === "polishing" ? (
            <>Tidying it up…</>
          ) : null}
        </div>
      )}

      {/* Link input row */}
      {showLink && (
        // flex-wrap + min-w-0: at phone width the fixed-width input pushed
        // the Add/Unlink buttons past the card edge — now the input takes the
        // first line and the buttons wrap beneath when they don't fit.
        <div className="flex flex-wrap items-center gap-1.5 border-b border-gray-100 px-2 py-1.5 bg-gray-50/60">
          <Link2 className="h-3.5 w-3.5 text-gray-400 shrink-0" />
          <input
            type="text"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); applyLink(); }
              if (e.key === "Escape") { setShowLink(false); setLinkUrl(""); }
            }}
            placeholder="Paste or type a link"
            className="min-w-0 flex-1 basis-40 rounded border border-gray-200 bg-white px-2 py-1 text-xs outline-none focus:border-primary/50"
            autoFocus
          />
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={applyLink}
            disabled={!linkUrl.trim()}
            className="rounded bg-primary px-2 py-1 text-xs font-medium text-white disabled:opacity-40"
          >
            Add link
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { editorRef.current?.focus(); restoreSelection(); exec("unlink"); setShowLink(false); setLinkUrl(""); }}
            className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-200"
            title="Remove link from selection"
          >
            Unlink
          </button>
        </div>
      )}

      {/* Hidden file input for image inserts */}
      {onImageUpload && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) void insertImageFile(file);
          }}
        />
      )}

      {/* Editing surface */}
      <div className="relative">
        {isEmpty && (
          <div className="pointer-events-none absolute left-4 top-3 text-sm text-gray-400">
            {placeholder}
          </div>
        )}
        <div
          ref={editorRef}
          contentEditable
          role="textbox"
          aria-multiline="true"
          className="px-4 py-3 text-sm text-gray-900 leading-relaxed outline-none overflow-y-auto [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:text-gray-600 [&_blockquote]:italic [&_hr]:my-2 [&_hr]:border-gray-200 [&_a]:text-blue-600 [&_a]:underline [&_img]:max-w-full [&_img]:rounded-md [&_img]:border [&_img]:border-gray-200 [&_img]:my-1.5"
          style={{ minHeight, maxHeight: "60vh" }}
          onInput={() => { emit(); checkForMention(); }}
          onKeyUp={checkForMention}
          onClick={(e) => { handleEditorClick(e); checkForMention(); }}
          onKeyDown={(e) => { if (resizeHandle) deselectImage(); handleKeyDown(e); }}
          onBlur={handleBlur}
          onDragOver={(e) => {
            // Allow dropping image files straight onto the note
            if (onImageUpload && Array.from(e.dataTransfer.types).includes("Files")) e.preventDefault();
          }}
          onDrop={(e) => {
            const file = Array.from(e.dataTransfer.files).find((f) => f.type.startsWith("image/"));
            if (!file || !onImageUpload) return;
            e.preventDefault();
            // Land the image where it was dropped, not where the caret was
            const doc = document as Document & { caretRangeFromPoint?: (x: number, y: number) => Range | null };
            const dropRange = doc.caretRangeFromPoint?.(e.clientX, e.clientY);
            if (dropRange && editorRef.current?.contains(dropRange.startContainer)) {
              savedRangeRef.current = dropRange;
            } else {
              saveSelection();
            }
            void insertImageFile(file);
          }}
          onPaste={(e) => {
            // Pasted image (screenshot / copied picture) → upload + embed
            const imageFile = Array.from(e.clipboardData.files).find((f) => f.type.startsWith("image/"));
            if (imageFile && onImageUpload) {
              e.preventDefault();
              saveSelection();
              void insertImageFile(imageFile);
              return;
            }
            // Paste as plain text — keeps pasted Word/web junk out of notes
            e.preventDefault();
            const text = e.clipboardData.getData("text/plain");
            document.execCommand("insertText", false, text);
          }}
        />
        {/* Drag-resize handle for the selected image */}
        {resizeHandle && (
          <div
            role="presentation"
            onMouseDown={startImageResize}
            className="absolute z-20 h-3.5 w-3.5 rounded-sm border-2 border-white bg-blue-500 shadow"
            style={{ top: resizeHandle.top, left: resizeHandle.left, cursor: "nwse-resize" }}
            title="Drag to resize"
          />
        )}
      </div>

      {/* Mention dropdown */}
      {mentionQuery !== null && filteredAgents.length > 0 && mentionPos && typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed z-[100] w-64 rounded-md border border-gray-200 bg-white shadow-xl overflow-hidden"
            style={{ top: mentionPos.top, left: mentionPos.left }}
          >
            <div className="max-h-56 overflow-y-auto py-1">
              {filteredAgents.map((agent, i) => (
                <button
                  key={agent.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => insertMention(agent)}
                  className={cn(
                    "w-full px-3 py-1.5 text-left text-sm flex flex-col",
                    i === selectedIndex ? "bg-blue-50" : "hover:bg-gray-50",
                  )}
                >
                  <span className="font-medium text-gray-900">{agent.name}</span>
                  <span className="text-xs text-gray-500">{agent.email}</span>
                </button>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
